/**
 * Customizable Dashboard — widget registry (metadata only).
 *
 * Stable widget IDs and their grid constraints. This is the fixed server/client
 * registry referenced by the security model: only IDs listed here are ever
 * accepted from saved layouts or rendered. The frontend maps these IDs to React
 * components separately (it never instantiates components from saved values).
 */
import { DASHBOARD_GRID_COLUMNS } from './constants.js';
/** Stable widget identifiers (do not rename — they are persisted). */
export const DASHBOARD_WIDGET_IDS = [
    'active-pull-requests',
    'average-code-quality',
    'total-work-items',
    'monitored-repositories',
    'recent-pull-requests',
    'recent-work-items',
    'quality-trend',
    'top-performers',
];
const METRIC_CONSTRAINTS = {
    type: 'metric',
    defaultW: 3,
    defaultH: 3,
    minW: 3,
    minH: 3,
    maxW: 12,
    maxH: 18,
};
const ACTIVITY_CONSTRAINTS = {
    type: 'activity',
    defaultW: 6,
    defaultH: 9,
    // Tables stay usable: at least half width, at least enough rows for the header + body.
    minW: 6,
    minH: 6,
    maxW: 12,
    maxH: 16,
};
const INSIGHT_CONSTRAINTS = {
    type: 'insight',
    defaultW: 7,
    defaultH: 13,
    minW: 4,
    minH: 8,
    maxW: 12,
    maxH: 18,
};
const TOP_PERFORMERS_CONSTRAINTS = {
    type: 'insight',
    defaultW: 5,
    defaultH: 13,
    minW: 4,
    minH: 8,
    maxW: 12,
    maxH: 18,
};
/**
 * Widget metadata keyed by ID. Array order defines the canonical reading order
 * used when appending newly introduced widgets to older saved layouts.
 */
export const DASHBOARD_WIDGET_DEFINITIONS = {
    'active-pull-requests': {
        id: 'active-pull-requests',
        ...METRIC_CONSTRAINTS,
        titleKey: 'customize.widgets.activePullRequests.title',
        descriptionKey: 'customize.widgets.activePullRequests.description',
        defaultEnabled: true,
        draggable: true,
        resizable: true,
        removable: true,
        required: false,
    },
    'average-code-quality': {
        id: 'average-code-quality',
        ...METRIC_CONSTRAINTS,
        titleKey: 'customize.widgets.averageCodeQuality.title',
        descriptionKey: 'customize.widgets.averageCodeQuality.description',
        defaultEnabled: true,
        draggable: true,
        resizable: true,
        removable: true,
        required: false,
    },
    'total-work-items': {
        id: 'total-work-items',
        ...METRIC_CONSTRAINTS,
        titleKey: 'customize.widgets.totalWorkItems.title',
        descriptionKey: 'customize.widgets.totalWorkItems.description',
        defaultEnabled: true,
        draggable: true,
        resizable: true,
        removable: true,
        required: false,
    },
    'monitored-repositories': {
        id: 'monitored-repositories',
        ...METRIC_CONSTRAINTS,
        titleKey: 'customize.widgets.monitoredRepositories.title',
        descriptionKey: 'customize.widgets.monitoredRepositories.description',
        defaultEnabled: true,
        draggable: true,
        resizable: true,
        removable: true,
        required: false,
    },
    'recent-pull-requests': {
        id: 'recent-pull-requests',
        ...ACTIVITY_CONSTRAINTS,
        titleKey: 'customize.widgets.recentPullRequests.title',
        descriptionKey: 'customize.widgets.recentPullRequests.description',
        defaultEnabled: true,
        draggable: true,
        resizable: true,
        removable: true,
        required: false,
    },
    'recent-work-items': {
        id: 'recent-work-items',
        ...ACTIVITY_CONSTRAINTS,
        titleKey: 'customize.widgets.recentWorkItems.title',
        descriptionKey: 'customize.widgets.recentWorkItems.description',
        defaultEnabled: true,
        draggable: true,
        resizable: true,
        removable: true,
        required: false,
    },
    'quality-trend': {
        id: 'quality-trend',
        ...INSIGHT_CONSTRAINTS,
        titleKey: 'customize.widgets.qualityTrend.title',
        descriptionKey: 'customize.widgets.qualityTrend.description',
        defaultEnabled: true,
        draggable: true,
        resizable: true,
        removable: true,
        required: false,
    },
    'top-performers': {
        id: 'top-performers',
        ...TOP_PERFORMERS_CONSTRAINTS,
        titleKey: 'customize.widgets.topPerformers.title',
        descriptionKey: 'customize.widgets.topPerformers.description',
        defaultEnabled: true,
        draggable: true,
        resizable: true,
        removable: true,
        required: false,
    },
};
const WIDGET_ID_SET = new Set(DASHBOARD_WIDGET_IDS);
/** Type guard: is `value` a known, registered widget ID? */
export function isDashboardWidgetId(value) {
    return typeof value === 'string' && WIDGET_ID_SET.has(value);
}
/** Returns the widget definition for a known ID. */
export function getDashboardWidgetDefinition(id) {
    return DASHBOARD_WIDGET_DEFINITIONS[id];
}
/** Clamp a width to the widget's allowed range and the canonical column count. */
export function clampWidgetWidth(def, width) {
    const maxColumns = Math.min(def.maxW, DASHBOARD_GRID_COLUMNS);
    return Math.min(maxColumns, Math.max(def.minW, Math.round(width)));
}
/** Clamp a height to the widget's allowed range. */
export function clampWidgetHeight(def, height) {
    return Math.min(def.maxH, Math.max(def.minH, Math.round(height)));
}
//# sourceMappingURL=widget-registry.js.map